import { getAddress } from '@ethersproject/address'
import { isFewToken } from '@ring-protocol/few-v2-sdk'
import { Protocol } from '@uniswap/router-sdk'
import { TradeType } from '@uniswap/sdk-core'
import {
  CommandType,
  RoutePlanner,
  SwapRouter,
  UNIVERSAL_ROUTER_ADDRESS,
  UniversalRouterVersion,
} from '@uniswap/universal-router-sdk'
import { CreateSwapRequest } from 'uniswap/src/data/tradingApi/__generated__'
import { GasFeeResult } from 'uniswap/src/features/gas/types'
import { ClassicTrade } from 'uniswap/src/features/transactions/swap/types/trade'
import { slippageToleranceToPercent } from 'uniswap/src/features/transactions/swap/utils/format'
import {
  ValidatedTransactionRequest,
  validatePermit,
  validateTransactionRequest,
} from 'uniswap/src/features/transactions/swap/utils/trade'

type PermitValues = {
  details: { token: string }
  spender: string
  sigDeadline: unknown
}

function getPermitValues(values: Record<string, unknown>): PermitValues | undefined {
  const details = values.details
  const spender = values.spender

  if (
    !details ||
    typeof details !== 'object' ||
    !('token' in details) ||
    typeof details.token !== 'string' ||
    typeof spender !== 'string' ||
    !('sigDeadline' in values)
  ) {
    return undefined
  }

  return values as PermitValues
}

function isPermitValidForSwap({
  chainId,
  inputTokenAddress,
  permit,
  permitDataPresent,
  permitValues,
  routerAddress,
  signature,
}: {
  chainId: number
  inputTokenAddress: string
  permit: ReturnType<typeof validatePermit>
  permitDataPresent: boolean
  permitValues?: PermitValues
  routerAddress: string
  signature?: string
}): boolean {
  if (!permitDataPresent) {
    return true
  }

  if (!permit || !permitValues || !signature) {
    return false
  }

  return (
    getAddress(permitValues.details.token) === getAddress(inputTokenAddress) &&
    getAddress(permitValues.spender) === getAddress(routerAddress) &&
    Number(permit.domain.chainId) === chainId
  )
}

export const LOCAL_FEW_V2_FALLBACK_GAS_FEE_WEI = 350_000 * 10e9
const LOCAL_FEW_V2_REQUEST_ID_PREFIX = 'local-fewv2-'

function getDirectFewV2Route(trade: ClassicTrade): ClassicTrade['routes'][number] | undefined {
  const [route] = trade.routes
  const inputToken = trade.inputAmount.currency.wrapped
  const outputToken = trade.outputAmount.currency.wrapped

  if (
    trade.tradeType !== TradeType.EXACT_INPUT ||
    trade.routes.length !== 1 ||
    route?.protocol !== Protocol.FEWV2 ||
    route.pools.length !== 1 ||
    route.path.length !== 2 ||
    !isFewToken(inputToken) ||
    !isFewToken(outputToken)
  ) {
    return undefined
  }

  return route
}

export function isDirectFewV2SwapTrade(trade: ClassicTrade): boolean {
  return Boolean(getDirectFewV2Route(trade))
}

export function isLocalFewV2SwapTrade(trade: ClassicTrade): boolean {
  return Boolean(trade.quote.requestId?.startsWith(LOCAL_FEW_V2_REQUEST_ID_PREFIX)) && isDirectFewV2SwapTrade(trade)
}

/**
 * The Trading API cannot simulate the custom FewV2 Universal Router command yet.
 * Keep the locally built transaction usable while its client-side gas estimate is
 * loading, then replace this conservative value with the real estimate.
 */
export function resolveLocalFewV2GasFee(localGasFee: GasFeeResult, quoteGasFee?: string): GasFeeResult {
  if (localGasFee.value !== undefined || localGasFee.error) {
    return localGasFee
  }

  const fallbackValue = quoteGasFee ?? LOCAL_FEW_V2_FALLBACK_GAS_FEE_WEI.toString()
  return {
    ...localGasFee,
    value: fallbackValue,
    displayValue: fallbackValue,
    error: null,
  }
}

/**
 * Builds the direct FewToken maintenance swap that the RW test API cannot yet
 * build. This intentionally supports only the narrow, already-validated shape
 * used by adjust-price: exact-input, one FewV2 pool, FewToken on both sides.
 */
export function createLocalFewV2SwapTransaction({
  signature,
  swapRequestArgs,
  trade,
}: {
  signature?: string
  swapRequestArgs: CreateSwapRequest
  trade: ClassicTrade
}): ValidatedTransactionRequest | undefined {
  if (!isLocalFewV2SwapTrade(trade)) {
    return undefined
  }

  const route = getDirectFewV2Route(trade)
  const inputToken = trade.inputAmount.currency.wrapped
  const outputToken = trade.outputAmount.currency.wrapped

  if (!route) {
    return undefined
  }

  const permit = validatePermit(swapRequestArgs.permitData)
  const permitValues = permit ? getPermitValues(permit.values) : undefined
  const recipient = trade.quote.quote.swapper
  const deadline = swapRequestArgs.deadline ?? trade.deadline
  const chainId = trade.inputAmount.currency.chainId
  const routerAddress = UNIVERSAL_ROUTER_ADDRESS(UniversalRouterVersion.V2_0, chainId)

  if (!recipient || !Number.isInteger(deadline) || deadline <= 0) {
    return undefined
  }

  try {
    if (
      !isPermitValidForSwap({
        chainId,
        inputTokenAddress: inputToken.address,
        permit,
        permitDataPresent: Boolean(swapRequestArgs.permitData),
        permitValues,
        routerAddress,
        signature,
      })
    ) {
      return undefined
    }

    const path = route.path.map((currency) => getAddress(currency.wrapped.address))
    if (path[0] !== getAddress(inputToken.address) || path[1] !== getAddress(outputToken.address)) {
      return undefined
    }

    const planner = new RoutePlanner()
    if (permitValues && signature) {
      planner.addCommand(CommandType.PERMIT2_PERMIT, [permitValues, signature])
    }
    planner.addCommand(CommandType.FEW_V2_SWAP_EXACT_IN, [
      getAddress(recipient),
      trade.inputAmount.quotient.toString(),
      trade.minimumAmountOut(slippageToleranceToPercent(trade.slippageTolerance)).quotient.toString(),
      path,
      true,
    ])

    const data = SwapRouter.INTERFACE.encodeFunctionData('execute(bytes,bytes[],uint256)', [
      planner.commands,
      planner.inputs,
      deadline,
    ])

    return validateTransactionRequest({
      chainId,
      data,
      from: getAddress(recipient),
      to: routerAddress,
      value: '0x0',
    })
  } catch {
    return undefined
  }
}
